import { useState, useEffect, useRef, useCallback } from 'react';
import { TrendDataPoint } from '../types';

interface TrendDataState {
  data: TrendDataPoint[] | null;
  loading: boolean;
  error: string | null;
  isMock: boolean;
}

/**
 * Session-level cache key prefix.
 * Bump version suffix to invalidate all existing cache entries.
 */
const SESSION_CACHE_PREFIX = 'trendData_v10_';
/** Keep session cache for 4 hours */
const SESSION_TTL_MS = 4 * 60 * 60 * 1000;

export function useTrendData(indicatorId: string | null, currentValue?: number): TrendDataState & { refetch: () => void } {
  const [state, setState] = useState<TrendDataState>({
    data: null,
    loading: false,
    error: null,
    isMock: false,
  });
  const abortRef = useRef<AbortController | null>(null);

  const fetchData = useCallback((ignoreCache = false) => {
    if (!indicatorId) {
      setState({ data: null, loading: false, error: null, isMock: false });
      return;
    }

    const cacheKey = SESSION_CACHE_PREFIX + indicatorId;

    if (!ignoreCache) {
      // ① sessionStorage cache check
      try {
        const raw = sessionStorage.getItem(cacheKey);
        if (raw) {
          const { data, timestamp, isMock } = JSON.parse(raw) as {
            data: TrendDataPoint[];
            timestamp: number;
            isMock: boolean;
          };
          if (Date.now() - timestamp < SESSION_TTL_MS && Array.isArray(data)) {
            setState({ data, loading: false, error: null, isMock: isMock ?? false });
            return;
          }
          sessionStorage.removeItem(cacheKey);
        }
      } catch {
        sessionStorage.removeItem(cacheKey);
      }
    }

    // ② API fetch
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    setState({ data: null, loading: true, error: null, isMock: false });

    fetch(`/api/trend/${indicatorId}?years=5`, {
      credentials: 'include',
      signal: abortRef.current.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<TrendDataPoint[]>;
      })
      .then((data) => {
        if (data && (data as any).error) {
          throw new Error((data as any).error);
        }
        if (!Array.isArray(data) || data.length === 0) {
          throw new Error('Empty or invalid response');
        }
        try {
          sessionStorage.setItem(
            cacheKey,
            JSON.stringify({ data, timestamp: Date.now(), isMock: false })
          );
        } catch { /* sessionStorage may be full */ }
        setState({ data, loading: false, error: null, isMock: false });
      })
      .catch((err: Error) => {
        if (err.name === 'AbortError') return;
        console.warn(`[useTrendData] Failed to fetch data for "${indicatorId}":`, err.message);
        
        // ③ Error state (Removed mock data fallback)
        setState({ data: null, loading: false, error: '데이터를 불러올 수 없습니다', isMock: false });
      });
  }, [indicatorId]);

  useEffect(() => {
    fetchData(false);
  }, [fetchData]);

  const refetch = useCallback(() => {
    fetchData(true);
  }, [fetchData]);

  return { ...state, refetch };
}
